import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * Echt · Global navigation state machine.
 *
 * This owns state and nothing else. Sticky pinning and `data-sticky-state` are
 * stock `header.js`; the focus trap is `@theme/focus`; the body lock is the
 * theme's `scroll-lock`; the query, its 200ms debounce and the Section
 * Rendering call are stock `<predictive-search-component>`; the bag count is
 * `cart-icon.js`. Nothing here duplicates any of it, and no third-party code
 * is involved.
 *
 * State (mirrored onto attributes, because every transition in the design is a
 * CSS change and none of it is a re-render):
 *
 *   data-view    "closed" | "top" | "gender" | "group"   — the drawer level
 *   data-search  "open" | absent                          — the search surface
 *   data-open    on one .echt-nav-item                    — the open panel
 *
 * @typedef {object} Refs
 * @property {HTMLElement} drawer - The drawer panel.
 * @property {HTMLElement} drawerTrigger - The hamburger.
 * @property {HTMLElement} levelTitle - The drawer level title.
 * @property {HTMLElement} backLabel - The back button's destination label.
 * @extends {Component<Refs>}
 */
class EchtHeaderComponent extends Component {
  requiredRefs = ['drawer', 'drawerTrigger'];

  /** @type {number | undefined} */
  #openTimer;

  /** @type {number | undefined} */
  #closeTimer;

  /** Fallback for the drawer's exit transition, for when there is none. @type {number | undefined} */
  #exitTimer;

  /** Cancels the pending exit listener, so overlapping closes never stack one. @type {AbortController | null} */
  #exitController = null;

  /** @type {'closed' | 'top' | 'gender' | 'group'} */
  #viewBeforeSearch = 'closed';

  #controller = new AbortController();
  #searchObserver;
  #searchFrame;


  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    document.addEventListener('keydown', this.#onDocumentKeyDown, { signal });
    document.addEventListener('pointerdown', this.#onSearchOutside, { signal });
    // "Any open mega menu closes on scroll" — the design's rule, not ours.
    document.addEventListener('scroll', this.#onScroll, { passive: true, capture: true, signal });
    // Tab past the last link in a panel closes it.
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    // Progressive enhancement for the bag. The trigger is a real <a href="/cart">
    // so it works with scripting off — the theme runs cart_type: 'page', so /cart
    // is a genuine destination. Horizon's declarative `on:click` does NOT
    // preventDefault on an anchor (measured: it navigates and the drawer never
    // opens), so the interception is here rather than in the markup.
    this.addEventListener('click', this.#onBagClick, { signal });
    // Native details still handles disclosure and keyboard interaction. Its
    // toggle event does not bubble, so capture it to reveal the expanded list.
    this.addEventListener('toggle', this.#onLocalizationToggle, { capture: true, signal });
  }

  /** @param {Event} event */
  #onLocalizationToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open ||
        !details.matches('.echt-localization__disclosure')) return;
    const scroller = details.closest('.echt-header__drawer-body');
    const heading = details.closest('.echt-localization--rows');
    if (!scroller || !heading) return;
    scroller.scrollTo({
      top: scroller.scrollTop + heading.getBoundingClientRect().top - scroller.getBoundingClientRect().top,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  };

  /**
   * Opens the cart drawer instead of following the bag's href, when the drawer
   * is present and upgraded. Anything else — a modified click, a missing or
   * un-upgraded drawer — falls through to the link.
   *
   * @param {MouseEvent} event
   */
  #onBagClick = (event) => {
    const trigger = /** @type {HTMLElement | null} */ (event.target)?.closest?.('a[aria-controls="cart-drawer"]');
    if (!trigger) return;
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (document.getElementById('cart-drawer'));
    if (typeof drawer?.open !== 'function') return;

    event.preventDefault();
    drawer.open();
    trigger.setAttribute('aria-expanded', 'true');
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#exitController?.abort();
    this.#clearTimers();
    this.#searchObserver?.disconnect();
    cancelAnimationFrame(this.#searchFrame);
    unlockScroll(this);
  }

  /* ---------------------------------------------------------------- state */

  get #desktop() {
    return window.matchMedia('(min-width: 1280px)').matches;
  }

  get view() {
    return this.dataset.view ?? 'closed';
  }

  set view(value) {
    this.dataset.view = value;
    this.refs.drawerTrigger.setAttribute('aria-expanded', String(value !== 'closed'));

    if (value === 'closed') {
      this.refs.drawer.removeAttribute('role');
      this.refs.drawer.removeAttribute('aria-modal');
    } else {
      this.refs.drawer.setAttribute('role', 'dialog');
      this.refs.drawer.setAttribute('aria-modal', 'true');
    }
  }

  /** @returns {HTMLElement | null} */
  get #openItem() {
    return this.querySelector('.echt-nav-item[data-open]');
  }

  /**
   * @param {HTMLElement | null} element
   * @param {string} selector
   */
  #expose(element, selector) {
    for (const open of this.querySelectorAll(`${selector}[data-open]`)) {
      if (open === element) continue;
      open.removeAttribute('data-open');
      open.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', 'false');
    }

    if (!element) return;

    element.setAttribute('data-open', '');
    element.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', 'true');
  }

  /**
   * @param {string} [title]
   * @param {string} [back]
   */
  #setLevel(title, back) {
    const { levelTitle, backLabel } = this.refs;

    if (levelTitle) levelTitle.textContent = title ?? '';
    if (backLabel) backLabel.textContent = back ?? this.dataset.menuLabel ?? '';
  }

  #clearTimers() {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#exitTimer);
  }

  /* --------------------------------------------------------- desktop mega */

  /** @param {Event} event */
  togglePanel(event) {
    const item = this.#itemOf(event);
    if (!item) return;

    if (!this.#desktop) {
      this.#openGender(item);
      return;
    }

    this.#clearTimers();
    if (item.hasAttribute('data-open')) this.closePanel();
    else this.#openPanel(item);
  }

  /** @param {Event} event */
  hoverPanel(event) {
    if (!this.#desktop) return;

    const item = this.#itemOf(event);
    if (!item) return;

    this.#clearTimers();
    this.#openTimer = setTimeout(() => this.#openPanel(item), 100);
  }

  /** @param {Event} event */
  leavePanel(event) {
    if (!this.#desktop) return;

    void event;
    this.#clearTimers();
    this.#closeTimer = setTimeout(() => this.closePanel(), 200);
  }

  /** @param {HTMLElement} item */
  #openPanel(item) {
    this.closeSearch();
    this.#expose(item, '.echt-nav-item');
  }

  closePanel() {
    this.#clearTimers();
    this.#expose(null, '.echt-nav-item');
  }

  /* ----------------------------------------------------------- the drawer */

  openDrawer() {
    // A close still waiting on its exit slide has nothing left to reset: the
    // lines below do it, now, for the level this open starts on.
    this.#exitController?.abort();
    clearTimeout(this.#exitTimer);
    this.closeSearch();
    this.#expose(null, '.echt-nav-item');
    this.#expose(null, '.echt-nav-group');
    this.#setLevel();
    this.view = 'top';
    lockScroll(this);
    trapFocus(this.refs.drawer);
  }

  closeDrawer() {
    this.view = 'closed';
    unlockScroll(this);
    removeTrapFocus();
    this.refs.drawerTrigger.focus();
    this.#resetLevelAfterExit();
  }

  /**
   * Puts the drawer back on its top level once the exit slide has played.
   *
   * The level *is* what the panel shows, so resetting it in `closeDrawer` would
   * snap a gender or group level back to the top rows while the panel is still
   * on screen sliding out. Nothing depends on the reset being synchronous:
   * `openDrawer` resets the level again before every open, so a reset that is
   * late — or that never lands — is invisible.
   *
   * The timer is the fallback for when there is no transition to wait for
   * (reduced motion, or a browser without discrete `display` transitions), and
   * the view check covers a reopen mid-exit, which `openDrawer` has already
   * reset for itself.
   */
  #resetLevelAfterExit() {
    clearTimeout(this.#exitTimer);
    this.#exitController?.abort();

    const drawer = this.refs.drawer;
    const controller = new AbortController();
    this.#exitController = controller;

    /** @param {TransitionEvent} [event] */
    const reset = (event) => {
      // `transitionend` bubbles, and links inside the drawer carry their own
      // fade, so only the panel's own slide counts as "the exit is over".
      if (event && (event.target !== drawer || event.propertyName !== 'translate')) return;

      controller.abort();
      clearTimeout(this.#exitTimer);
      if (this.view !== 'closed') return;

      this.#expose(null, '.echt-nav-item');
      this.#expose(null, '.echt-nav-group');
      this.#setLevel();
    };

    drawer.addEventListener('transitionend', reset, { signal: controller.signal });
    this.#exitTimer = setTimeout(reset, 400);
  }

  /** @param {HTMLElement} item */
  #openGender(item) {
    this.#expose(item, '.echt-nav-item');
    this.#expose(null, '.echt-nav-group');
    this.view = 'gender';
    this.#setLevel(item.dataset.label);
  }

  /** @param {Event} event */
  openGroup(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const group = target.closest('.echt-nav-group');
    if (!(group instanceof HTMLElement)) return;

    this.#expose(group, '.echt-nav-group');
    this.view = 'group';
    this.#setLevel(group.dataset.label, this.#openItem?.dataset.label);
  }

  /** The back label always names its destination, never "Back". */
  goBack() {
    if (this.view === 'group') {
      this.#expose(null, '.echt-nav-group');
      this.view = 'gender';
      this.#setLevel(this.#openItem?.dataset.label);
      return;
    }

    this.#expose(null, '.echt-nav-item');
    this.view = 'top';
    this.#setLevel();
  }

  /* ----------------------------------------------------------- the search */

  openSearch() {
    if (this.dataset.search === 'open') return;

    this.closePanel();
    this.#viewBeforeSearch = /** @type {'closed'} */ (this.view);
    this.dataset.search = 'open';
    this.#syncSearchTriggers(true);
    this.#sizeSearch();
    const body = this.querySelector('.echt-search__body');
    this.#searchObserver ??= new MutationObserver(this.#queueSearchSize);
    if (body) this.#searchObserver.observe(body, { childList: true, subtree: true });
    window.addEventListener('resize', this.#queueSearchSize, { signal: this.#controller.signal });
    window.visualViewport?.addEventListener('resize', this.#queueSearchSize, { signal: this.#controller.signal });

    if (!window.matchMedia('(min-width: 768px)').matches) {
      lockScroll(this);
      trapFocus(this.querySelector('.echt-search') ?? this);
    }

    /** @type {HTMLInputElement | null} */
    const input = this.querySelector('.echt-search__input');
    input?.focus();
  }

  #queueSearchSize = () => {
    cancelAnimationFrame(this.#searchFrame);
    this.#searchFrame = requestAnimationFrame(() => this.#sizeSearch());
  };

  // Measure the content, not the scrolling body's stretched height, so
  // clearing a query can return to the compact state after expansion.
  #sizeSearch() {
    if (this.dataset.search !== 'open') return;
    const panel = this.querySelector('.echt-search');
    const body = this.querySelector('.echt-search__body');
    const head = this.querySelector('.echt-search__head');
    if (!panel || !body || !head) return;
    if (window.matchMedia('(min-width: 768px)').matches) {
      panel.style.removeProperty('--echt-search-height');
      return;
    }
    const hasResults = !!body.querySelector('[data-echt-results] .echt-search__result');
    const style = getComputedStyle(body);
    const contentHeight = [...body.children].reduce((height, child) => height + child.getBoundingClientRect().height, 0);
    const compactHeight = head.getBoundingClientRect().height + contentHeight +
      parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const height = hasResults ? viewportHeight : Math.min(compactHeight, viewportHeight);
    panel.style.setProperty('--echt-search-height', `${Math.ceil(height)}px`);
  }

  /** Closing returns to the view the shopper opened it from, not the homepage. */
  closeSearch() {
    if (this.dataset.search !== 'open') return;

    this.#searchObserver?.disconnect();
    window.removeEventListener('resize', this.#queueSearchSize);
    window.visualViewport?.removeEventListener('resize', this.#queueSearchSize);
    cancelAnimationFrame(this.#searchFrame);
    delete this.dataset.search;
    // Reset through the stock component so its pending request and keyboard
    // selection are cleared too, without focusing the now-closed input.
    const search = this.querySelector('.echt-search__component');
    search?.search?.cancel();
    search?.resetSearch?.(false);
    this.#syncSearchTriggers(false);
    removeTrapFocus();

    if (this.#viewBeforeSearch === 'closed') {
      unlockScroll(this);
    } else {
      trapFocus(this.refs.drawer);
    }
  }

  /** @param {PointerEvent} event */
  #onSearchOutside = (event) => {
    if (this.dataset.search !== 'open' || !window.matchMedia('(min-width: 768px)').matches) return;
    const target = event.target;
    if (!(target instanceof Element) || target.closest('.echt-search, [aria-controls^="EchtSearch-"]')) return;
    this.closeSearch();
  };

  toggleSearch() {
    if (this.dataset.search === 'open') this.closeSearch();
    else this.openSearch();
  }

  /** @param {boolean} open */
  #syncSearchTriggers(open) {
    for (const trigger of this.querySelectorAll('[aria-controls^="EchtSearch-"]')) {
      trigger.setAttribute('aria-expanded', String(open));
    }
  }

  /* ------------------------------------------------------------- plumbing */

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #itemOf(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    const item = target.closest('.echt-nav-item');
    return item instanceof HTMLElement ? item : null;
  }

  /** @param {KeyboardEvent} event */
  #onDocumentKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    if (this.dataset.search === 'open') {
      this.closeSearch();
      return;
    }

    if (this.view !== 'closed') {
      this.closeDrawer();
      return;
    }

    const item = this.#openItem;
    if (!item) return;

    this.closePanel();
    /** @type {HTMLElement | null} */ (item.querySelector('.echt-nav-item__trigger'))?.focus();
  };

  #onScroll = () => {
    if (this.#desktop && this.#openItem) this.closePanel();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!this.#desktop) return;

    const item = this.#openItem;
    if (!item) return;

    const next = event.relatedTarget;
    if (next instanceof Node && item.contains(next)) return;

    this.closePanel();
  };
}

if (!customElements.get('echt-header-component')) {
  customElements.define('echt-header-component', EchtHeaderComponent);
}
